import { throttle } from "lodash-es";

import { serverUpdateDraftPublication } from "api/api-server-publishing";
import { SectionKind, type SectionType } from "components/server-types";
import { store } from "store";
import { sectionSelectors } from "store/selectors/base";
import { isCCMEnabled } from "utils/waffle-flags";

const getSectionKind = (
  sectionOrSectionKind: Pick<SectionType, "kind"> | SectionKind | null | undefined,
) => {
  if (!sectionOrSectionKind) {
    return undefined;
  }

  return typeof sectionOrSectionKind === "string"
    ? sectionOrSectionKind
    : sectionOrSectionKind.kind;
};

export const isLinkedSection = (
  sectionOrSectionKind: Pick<SectionType, "kind"> | SectionKind | null | undefined,
) => {
  return getSectionKind(sectionOrSectionKind) === SectionKind.LINKED;
};

export const isTemplateSection = (
  sectionOrSectionKind: Pick<SectionType, "kind"> | SectionKind | null | undefined,
) => {
  return getSectionKind(sectionOrSectionKind) === SectionKind.TEMPLATE;
};

export const isUnlinkedSection = (
  sectionOrSectionKind: Pick<SectionType, "kind"> | SectionKind | null | undefined,
) => {
  return getSectionKind(sectionOrSectionKind) === SectionKind.UNLINKED;
};

/**
 * These are the cached results of getUpdateDraftPublicationFunction, below. We need to cache
 * these functions to avoid the race condition where multiple lodash throttles for the same
 * Section are active at the same time, potentially leading to multiple concurrent requests
 * to the publishing API that break the rate limit.
 */
const updateDraftPublicationFunctionCache = new Map<string, () => void>();

// While a user is editing a worksheet, the first edit triggers an update of the Publication
// immediately, and subsequent edits trigger updates no more than once every ten seconds. We
// want the first updates to be registered immediately so that the UI is updated for all users
// as soon as possible and so that we don't lose edits if the author navigates away, but the
// subsequent edits are not so cricital and we don't want to hammer the server with requests.
export const UPDATE_DRAFT_PUBLICATION_THROTTLE_DELAY = 10_000;

/**
 * Generate a function that invokes the publishing API to update the current draft Publication
 * for the given Section. The function will be called whenever an edit is made to a worksheet
 * within the Section. It only invokes the API if CCM is enabled and the given Section is a
 * CCM template, and it's throttled to do so at most once every 3 seconds to avoid flooding
 * the server with a separate request for every keystroke.
 */
export const getUpdateDraftPublicationFunction = (sectionId: string): (() => void) => {
  const cachedFunction = updateDraftPublicationFunctionCache.get(sectionId);
  if (cachedFunction) {
    return cachedFunction;
  }

  const newFunction = throttle(() => {
    const state = store.getState();
    const section = sectionSelectors.selectById(state, sectionId);
    if (section?.kind === SectionKind.TEMPLATE && isCCMEnabled()) {
      serverUpdateDraftPublication(sectionId);
    }
  }, UPDATE_DRAFT_PUBLICATION_THROTTLE_DELAY);

  updateDraftPublicationFunctionCache.set(sectionId, newFunction);

  return newFunction;
};

/**
 * Clear the cache; used only in tests.
 */
export const clearUpdateDraftPublicationFunctionCache = () => {
  updateDraftPublicationFunctionCache.clear();
};
